import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';

import { FilterOptionModel } from '../../Common/Filter/models/filter-option.model';
import { FilterTypeCheckboxModel } from '../../Common/Filter/models/filter-type-checkbox.model';
import { FilterTypeDateQueryModel } from '../../Common/Filter/models/filter-type-date-query.model';
import { FilterService } from '../../Common/Filter/services/abstract-filter.service';
import { DateQueryModel } from '../../Common/Forms/models/date-query.model';
import { getDefaultDateInputOptionValuesModel } from '../../Common/Forms/models/date-query-input-options.model';
import { AdHocRunRow } from '../models/ad-hoc-run-row.model';
import { CurationAgentModel } from '../models/curation-agent.model';
import { AgentUsageService } from './agent-usage.service';

/**
 * The filters the ad hoc management panel is narrowed by - the shared filter widget and its chips
 * are driven off this, the way the activity log and the mention feed drive off their own.
 *
 * The runs are held here rather than fetched: the panel reads them once and hands them over, and
 * every filter is applied over that list in the browser.
 */
@Injectable()
export class AdHocRunFilterService extends FilterService {
    readonly agentFilter: FilterTypeCheckboxModel<string> = new FilterTypeCheckboxModel<string>(
        'agent',
        'AutomationAgents.adHoc.agent'
    );

    readonly sourceFilter: FilterTypeCheckboxModel<string> = new FilterTypeCheckboxModel<string>(
        'source',
        'AutomationAgents.card.column.source'
    );

    readonly startedFilter: FilterTypeDateQueryModel = new FilterTypeDateQueryModel(
        'started',
        'AutomationAgents.adHoc.management.filter.dateStarted',
        getDefaultDateInputOptionValuesModel()
    );

    readonly completedFilter: FilterTypeDateQueryModel = new FilterTypeDateQueryModel(
        'completed',
        'AutomationAgents.adHoc.management.filter.dateCompleted',
        getDefaultDateInputOptionValuesModel()
    );

    readonly statusFilter: FilterTypeCheckboxModel<string> = new FilterTypeCheckboxModel<string>(
        'status',
        'AutomationAgents.card.column.status'
    );

    readonly createdByFilter: FilterTypeCheckboxModel<string> = new FilterTypeCheckboxModel<string>(
        'createdBy',
        'AutomationAgents.adHoc.management.filter.createdBy'
    );

    /** What the table draws, once every filter has been applied */
    private readonly filteredRuns: BehaviorSubject<AdHocRunRow[]> = new BehaviorSubject<AdHocRunRow[]>([]);
    readonly filteredRuns$: Observable<AdHocRunRow[]> = this.filteredRuns.asObservable();

    private runs: AdHocRunRow[] = [];

    constructor() {
        super();

        this.filters.push(
            this.agentFilter,
            this.sourceFilter,
            this.startedFilter,
            this.completedFilter,
            this.statusFilter,
            this.createdByFilter
        );
        this.setFilters(this.filters);
    }

    /**
     * The runs to filter, with the options each checkbox filter offers rebuilt from them. Agents are
     * offered whether or not they have run, so the agent the panel opens on always has its chip
     */
    setRuns(runs: AdHocRunRow[], agents: CurationAgentModel[]): void {
        this.runs = runs ?? [];

        this.agentFilter.options = this.agentOptions(agents);
        this.sourceFilter.options = this.optionsFor(row => row.sourceName, row => row.sourceName);
        this.statusFilter.options = this.optionsFor(row => row.statusValue, row => row.status);
        this.createdByFilter.options = this.optionsFor(row => String(row.createdByUserId), row => row.createdByName);

        this.setFilters(this.filters);
        this.applyFilters();
    }

    /** Opened from an agent's dashboard, so the panel starts narrowed to that agent */
    selectAgent(agentId: number): void {
        if (!agentId) {
            return;
        }

        this.agentFilter.setValue([String(agentId)]);
        this.agentFilter.setCurrentValue([String(agentId)]);
        this.setFilters(this.filters);
        this.applyFilters();
    }

    override applyFilters(): void {
        super.applyFilters();
        this.filteredRuns.next(this.filtered());
    }

    override clearFilters(): void {
        super.clearFilters();
        this.filteredRuns.next(this.filtered());
    }

    private filtered(): AdHocRunRow[] {
        const agents = this.agentFilter.getValue() ?? [];
        const sources = this.sourceFilter.getValue() ?? [];
        const statuses = this.statusFilter.getValue() ?? [];
        const createdBy = this.createdByFilter.getValue() ?? [];

        return this.runs.filter(row =>
            (!agents.length || agents.includes(String(row.run.agentId)))
            && (!sources.length || sources.includes(row.sourceName))
            && (!statuses.length || statuses.includes(row.statusValue))
            && (!createdBy.length || createdBy.includes(String(row.createdByUserId)))
            && this.withinRange(row.started, this.startedFilter.getValue())
            && this.withinRange(row.completed, this.completedFilter.getValue()));
    }

    /** Whether a run's date sits inside a range. A range of All, or no date at all, narrows nothing */
    private withinRange(date: number, range: DateQueryModel): boolean {
        const bounds = range ? AgentUsageService.bounds(range) : null;

        if (!bounds) {
            return true;
        }

        return !!date && date >= bounds.start && date <= bounds.end;
    }

    /** Every agent, with how many runs each has */
    private agentOptions(agents: CurationAgentModel[]): FilterOptionModel<string>[] {
        const counts = new Map<string, number>(
            this.optionsFor(row => String(row.run.agentId), row => row.agentName)
                .map(option => [option.value, option.count])
        );

        return (agents ?? [])
            .map(agent => ({ value: String(agent.id), label: agent.name, count: counts.get(String(agent.id)) ?? 0 }))
            .sort((first, second) => first.label.localeCompare(second.label));
    }

    /** What a checkbox filter offers, with how many runs carry each value */
    private optionsFor(
        value: (row: AdHocRunRow) => string,
        label: (row: AdHocRunRow) => string
    ): FilterOptionModel<string>[] {
        const counts = new Map<string, { label: string; count: number }>();

        this.runs.forEach(row => {
            const key = value(row);

            if (!key) {
                return;
            }

            const entry = counts.get(key) ?? { label: label(row), count: 0 };

            entry.count++;
            counts.set(key, entry);
        });

        return [...counts.entries()]
            .map(([key, entry]) => ({ value: key, label: entry.label, count: entry.count }))
            .sort((first, second) => first.label.localeCompare(second.label));
    }
}
